import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseResponseDTO } from 'src/app/core/models/app.models';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { Utils } from 'src/app/shared/helpers/utils.helper';
import { ApiService } from 'src/app/shared/services/api.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { environment } from 'src/environments/environment';
import { LoginOTPDTO } from 'src/app/modules/authentication/components/models/autentication-dto.models';
import { UserService } from 'src/app/shared/services/user.service';
import { lastValueFrom } from 'rxjs';
import en from 'src/assets/i18n/en';  
import { FormValidationService } from 'src/app/shared/services/form-validation.service'; 
import { VendorService } from 'src/app/shared/services/vendor.service';
import { AuthenticationActions } from 'src/app/modules/authentication/components/models/authentication-actions.enum';
import { SharedOTPDialogService } from '../../services/shared-otp-dialog.service';

@Component({
  selector: 'app-shared-otp-dialog',
  templateUrl: './shared-otp-dialog.component.html',
  styleUrls: ['./shared-otp-dialog.component.scss'] 
})
export class SharedOTPDialogComponent implements OnInit {
  private _otpForm!: FormGroup;
  isSubmitting: boolean = false;
  verifyEmailPage: boolean = false;
  email: string | null = null;
  data: any | null = null;
  action!: AuthenticationActions;
  successMessage: string = "OTP Verified Successfully!";  // Success message to show after verification



  //#region getters
  public get otpForm(): FormGroup {
    return this._otpForm;
  }
  //#endregion

  //#region setters
  public set otpForm(value: FormGroup) {
    this._otpForm = value;
  }
  //#endregion

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private _router: Router,
    private _activeRoute: ActivatedRoute,
    private _formBuilder: FormBuilder,
    private _http: HttpClient,
    private _loaderService: LoaderService,
    private _userService: UserService,
    private formValidationService: FormValidationService,
    private vendorService: VendorService,
    public dialogService: SharedOTPDialogService, // Reintroduced dialog service

  ) {}

  ngOnInit(): void {
    this.verifyEmailPage = false;

    this._activeRoute.queryParams.subscribe(params => {
      this.action = +params['action']; // '+' converts the string to a number
      console.log('Action:', this.action);

      if (this.action == AuthenticationActions.VerifyEmail) {
        this.verifyEmailPage = true;
      }

      if (this.action == AuthenticationActions.Login || this.action == AuthenticationActions.ForgetPassword) {
        this.email = this._userService.email;

        if (!this.email) {
          this._router.navigate([AppConstants.ROUTES.AUTHENTICATION, AppConstants.ROUTES.LOGIN]);
        }
      }

    });

    this.otpForm = this.fb.group({
      otp1: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]],
      otp2: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]],
      otp3: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]],
      otp4: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]],
      otp5: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]],
      otp6: ['', [Validators.required, Validators.minLength(1), Validators.maxLength(1)]]
    });

    // Subscribe to changes in any of the OTP fields
    this.otpForm.valueChanges.subscribe(() => {
      this.dialogService.clearMessages();  // Clear messages when typing starts
    });
    
  }



  // Allow only numeric input - professional solution
  onOtpKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    // Allow only numbers (0-9)
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  // Additional input validation to prevent paste of non-numeric characters
  onOtpInput(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    // Remove any non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
    if (value !== numericValue) {
      this.otpForm.get(controlName)?.setValue(numericValue);
    }
  }

  onSubmit() {
    if (this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;

    if (this.otpForm.invalid) {
      this.markFormGroupTouched(this.otpForm);
      this.isSubmitting = false;
      return;
    }

    const otp = `${this.otpForm.value.otp1}${this.otpForm.value.otp2}${this.otpForm.value.otp3}${this.otpForm.value.otp4}${this.otpForm.value.otp5}${this.otpForm.value.otp6}`;
    this.verifyOtp(otp);
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
    });
  }

  private verifyOtp(otp: string) {
    console.log('OTP submitted:', otp);

    const loginOTPDTO = new LoginOTPDTO(this._userService.user?.EmailAddress || '', otp, this.dialogService.processName,this.dialogService.data);

    this.handleOTPVerfication(loginOTPDTO);
  }

  async resendOTP() {
    const [isSuccess, message] = await this.dialogService.sendOTP();
    console.log("pelase check "+isSuccess+" and msg "+message);
    if (isSuccess) {
      this.dialogService.resendSuccess = true;
      this.dialogService.resendError = false;
      this.dialogService.resendSuccessMessage = 'A new OTP has been sent to your email and mobile number.';
      this.dialogService.otpError = null;
    } else {
      this.dialogService.otpError = null;
      this.dialogService.resendError = true;
      this.dialogService.resendSuccess = false;
      this.dialogService.resendErrorMessage = message || 'Failed to resend OTP.';
    }
  }

  private createApiService(): ApiService {
    const apiService = new ApiService(this._http);
    apiService['_baseUrl'] = environment.API_URL; // Set the base URL manually
    return apiService;
  }

  private async handleOTPVerfication(data: LoginOTPDTO) {
    this._loaderService.showLoader();
    const apiService = this.createApiService();
    const payload = this.prepareValidateOTPRequestPayload(data);
    this.fireValidateUserRequest(apiService, payload)
      .then((response: any) => {
        let result = response as unknown as BaseResponseDTO;
        if (result.isSuccess) {
          this.dialogService.isProcessCompleted = true;
          this.successMessage = response.errorMessageEn;
          if (Utils.IsNullOrWhiteSpace(this._userService.user?.Id)) {
            // Handle missing user ID
          }
        } else {
          this.dialogService.otpError = result.errorMessageEn;
        }
      })
      .catch((err: HttpErrorResponse) => {
        this._loaderService.hideLoader();
      })
      .finally(() => {
        this._loaderService.hideLoader();
        this.isSubmitting = false;

      });
  }

  //#region Payloads
  private prepareValidateOTPRequestPayload(data: any) {
    return data as LoginOTPDTO;
  }
  //#endregion

  private fireValidateUserRequest(apiService: ApiService, payload: LoginOTPDTO) {
    let req$ = apiService.post(AppConstants.CONTROLLERS.ACCOUNT, AppConstants.ACTIONS.VerifyOTP, true, environment.APIs_PREFIX, payload);
    return lastValueFrom(req$);
  }

  private fireResendOTPRequest(apiService: ApiService, payload: { Email: string; Mobile: string }) {
    let req$ = apiService.post(AppConstants.CONTROLLERS.ACCOUNT, AppConstants.ACTIONS.Generateotp, true, environment.APIs_PREFIX, payload);
    return lastValueFrom(req$); // Convert observable to a promise
  }
}
